<template>
  <div class="purchase-texts-container">
    <h3 class="section-title">Purchase Page Texts</h3>
    <div class="config-card">
      <p class="config-hint intro">
        These texts appear on the video and package purchase page (PPV). Leave a field blank to
        use the app's built-in default. They apply to all languages.
      </p>

      <div class="text-grid">
        <div class="config-item">
          <label class="config-label">Subscribe button label</label>
          <input type="text" v-model="texts.subscribe_button_label" @change="save" placeholder="Subscribe" class="text-input"/>
          <p class="config-hint">The price and interval are appended automatically.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Subscribe subtitle</label>
          <input type="text" v-model="texts.subscribe_subtitle" @change="save" placeholder="Access to all premium content for a low monthly price" class="text-input"/>
          <p class="config-hint">A free-trial notice is still prepended automatically when an offer has one.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Rent button label</label>
          <input type="text" v-model="texts.ppv_button_label" @change="save" placeholder="Rent" class="text-input"/>
          <p class="config-hint">The price is appended automatically.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Rent subtitle</label>
          <input type="text" v-model="texts.ppv_subtitle" @change="save" placeholder="Rent for {days} days" class="text-input"/>
          <p class="config-hint"><code>{days}</code> is replaced with the watching period.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Package subtitle</label>
          <input type="text" v-model="texts.package_subtitle" @change="save" placeholder="{count} videos in this bundle" class="text-input"/>
          <p class="config-hint"><code>{count}</code> is replaced with the number of items.</p>
        </div>

        <div class="config-item item--wide">
          <label class="config-label">Terms of service notice</label>
          <textarea v-model="texts.purchase_terms_text" @change="save" placeholder="By purchasing you agree to our {terms}." class="text-input text-area"></textarea>
          <p class="config-hint">
            Small print shown below the purchase options. Use <code>{terms}</code> to insert a link
            to your configured terms of service (set under Documents → Legal Links). Basic HTML links are allowed.
          </p>
        </div>

        <div class="config-item">
          <label class="config-label">Help link text</label>
          <input type="text" v-model="texts.purchase_help_text" @change="save" placeholder="Need help? Read the FAQ" class="text-input"/>
          <p class="config-hint">Leave blank to hide the help link.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Help link URL</label>
          <input type="text" v-model="texts.purchase_help_url" @change="save" placeholder="(defaults to the in-app FAQ page)" class="text-input"/>
          <p class="config-hint">Opens in a new tab. Blank = the in-app FAQ page.</p>
        </div>
      </div>

      <transition name="fade">
        <div v-if="saving" class="config-status saving">Saving...</div>
        <div v-else-if="saved" class="config-status saved">Saved</div>
      </transition>
    </div>

    <!-- Live preview -->
    <h3 class="section-title">Preview</h3>
    <div class="preview-card">
      <button class="preview-button">
        {{ texts.subscribe_button_label || 'Subscribe' }}<br>
        <span class="preview-price">€ 9,99 / month</span>
      </button>
      <div class="preview-hint">
        {{ texts.subscribe_subtitle || 'Access to all premium content for a low monthly price' }}
      </div>
      <button class="preview-button preview-button--secondary">
        {{ texts.ppv_button_label || 'Rent' }}<br>
        <span class="preview-price">€ 4,99</span>
      </button>
      <div class="preview-hint">
        {{ (texts.ppv_subtitle || 'Rent for {days} days').replace('{days}', '7') }}
      </div>
      <div class="preview-hint">
        {{ (texts.package_subtitle || '{count} videos in this bundle').replace('{count}', '5') }}
      </div>
      <div v-if="texts.purchase_terms_text" class="preview-terms" v-html="previewTerms"></div>
      <div v-if="texts.purchase_help_text" class="preview-help">{{ texts.purchase_help_text }}</div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      texts: {
        subscribe_button_label: '',
        subscribe_subtitle: '',
        ppv_button_label: '',
        ppv_subtitle: '',
        package_subtitle: '',
        purchase_terms_text: '',
        purchase_help_text: '',
        purchase_help_url: ''
      },
      saving: false,
      saved: false,
      saveTimeout: null
    }
  },

  computed: {
    previewTerms() {
      // Mirror the frontend: {terms} becomes a (here non-clickable) link label.
      return (this.texts.purchase_terms_text || '').replace(/\{terms\}/g, '<u>terms of service</u>')
    }
  },

  methods: {
    load() {
      fetch('/admin/api/v2/config/ppv_texts')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && resp.data.value) {
            const v = resp.data.value
            this.texts = {
              subscribe_button_label: v.subscribe_button_label ?? '',
              subscribe_subtitle: v.subscribe_subtitle ?? '',
              ppv_button_label: v.ppv_button_label ?? '',
              ppv_subtitle: v.ppv_subtitle ?? '',
              package_subtitle: v.package_subtitle ?? '',
              purchase_terms_text: v.purchase_terms_text ?? '',
              purchase_help_text: v.purchase_help_text ?? '',
              purchase_help_url: v.purchase_help_url ?? ''
            }
          }
        })
        .catch(() => {
          // Silently use defaults if config doesn't exist
        })
    },

    save() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }

      this.saving = true
      this.saved = false

      fetch('/admin/api/v2/config/ppv_texts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            value: this.texts,
            is_public: true
          }
        })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.saving = false
          this.saved = true
          this.saveTimeout = setTimeout(() => {
            this.saved = false
          }, 2000)
        })
        .catch(() => {
          this.saving = false
          this.$root.app.showAlert('Error saving purchase texts', 'danger')
        })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Purchase Texts', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.load()
  }
}
</script>

<style scoped>
.purchase-texts-container {
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.config-card,
.preview-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
}

.intro {
  margin-top: 0;
  margin-bottom: 1.25rem;
}

.text-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.item--wide {
  grid-column: 1 / -1;
}

.config-label {
  font-size: 0.85rem;
  color: #E2E8F0;
  margin-bottom: 0.5rem;
  display: block;
  font-weight: 600;
}

.config-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.4rem;
}

.config-hint code {
  background: rgba(0, 0, 0, 0.3);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  font-size: 0.72rem;
  color: #20B4FF;
}

.text-input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.85rem;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.text-input.text-area {
  min-height: 4.5rem;
  resize: vertical;
  font-family: inherit;
}

.config-status {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.config-status.saving {
  color: #9ca3af;
}

.config-status.saved {
  color: #28a745;
  background: #28a74520;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Preview */
.preview-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.preview-button {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 2rem;
  font-weight: 600;
  cursor: default;
  text-align: center;
  line-height: 1.3;
  min-width: 220px;
}

.preview-button--secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.preview-price {
  font-size: 0.85rem;
  opacity: 0.9;
}

.preview-hint {
  font-size: 0.8rem;
  color: #94A3B8;
}

.preview-terms {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
  text-align: center;
}

.preview-help {
  font-size: 0.8rem;
  color: #20B4FF;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .text-grid {
    grid-template-columns: 1fr;
  }
}
</style>
